import type {PrizeSearchProps, SearchResult} from 'services/elastic/types';
import type {Schemas} from 'api/dto.generated';
import {normalizeQuery} from 'api/functions';
import {transformErrorResponse} from 'store/api';
import {ebsApi} from 'store/ebsApi';
import type {Prize} from './types';
import {
  mapGetPrizeDto,
  mapPrizeSearchDto,
  mapToCreatePrizeDto,
  mapToUpdatePrizeDto,
} from './mappers';

const prizesApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getPrize: build.query<Prize, string, Schemas.LiteraturePrizeWithWinners>({
      query: id => `literatureprize/${id}`,
      transformResponse: mapGetPrizeDto,
      transformErrorResponse,
      providesTags: (_, __, id) => [{type: 'Prize', id}],
    }),

    postPrize: build.mutation<Prize, Prize, Schemas.LiteraturePrizeWithWinners>(
      {
        query: prize => ({
          url: 'literatureprize',
          method: 'POST',
          body: mapToCreatePrizeDto(prize),
        }),
        transformResponse: mapGetPrizeDto,
        transformErrorResponse,
      },
    ),

    putPrize: build.mutation<Prize, Prize, Schemas.LiteraturePrizeWithWinners>({
      query: prize => ({
        url: `literatureprize/${prize.id}`,
        method: 'PUT',
        body: mapToUpdatePrizeDto(prize),
      }),
      transformResponse: mapGetPrizeDto,
      transformErrorResponse,
      invalidatesTags: (_, __, prize) => [{type: 'Prize', id: prize.id}],
    }),

    deletePrize: build.mutation<void, string>({
      query: id => ({
        url: `literatureprize/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
      invalidatesTags: (_, __, id) => [{type: 'Prize', id}],
    }),

    searchPrize: build.query<
      SearchResult<Prize>,
      PrizeSearchProps,
      Schemas.LiteraturePrizesPageResponse
    >({
      keepUnusedDataFor: 5,
      query: ({query, page, size, abortSignal}) => {
        const trimmed = query.trim();
        const emptySearch = trimmed === '' || trimmed === '*';
        const normalizedQuery = normalizeQuery(query);

        return {
          url: 'literatureprize/search',
          params: {
            name: emptySearch ? '' : normalizedQuery,
            page,
            size,
            ...(emptySearch ? {sort: 'name'} : {}),
          },
          signal: abortSignal,
        };
      },
      transformResponse: (dto, _meta, {query}) =>
        mapPrizeSearchDto(normalizeQuery(query), dto),
      transformErrorResponse,
    }),
  }),
});

export const {
  useGetPrizeQuery,
  usePostPrizeMutation,
  usePutPrizeMutation,
  useDeletePrizeMutation,
  useLazySearchPrizeQuery,
} = prizesApi;
